import Axios from "axios";
import { Button } from "components";
import {
    BookingFlowNarrowContent,
    BookingFlowPageHeading,
    BookingFlowPaymentFooterDisclaimers,
    BookingFlowScreeningPayment,
} from "components/booking-flow";
import { getHpvBookingFlowDueSummary } from "utils/booking-flow-payment-due-summary";
import useAnalytics from "hooks/analytics";
import useBookingFlowRedesign from "hooks/useBookingFlowRedesign";
import useAuthUser from "hooks/auth-user";
import { FollowUpType } from "models/appointment";
import { useEffect, useState } from "react";
import { useAsyncCallback } from "react-async-hook";
import { trackScreeningPaymentSubmitted } from "utils/analytics/booking-flow-tracking";
import { buildReservedAppointmentDescription } from "utils/appointment-display";
import ENDPOINTS from "utils/endpoints";
import { getAppointmentHold } from "utils/session-storage";
import { CardElementsProvider } from "context/stripe-provider";
import useLanguage from "../../../../hooks/language";
import PaymentFieldComponent from "../payment-field/payment-field";
import ExpressWalletCheckout, {
    WalletCheckoutFlow,
} from "../payment-field/express-wallet-checkout";
import PaymentMethodsBusy from "../payment-field/payment-methods-busy";
import useCardPlaceholder from "../payment-field/use-card-placeholder";
import useCardToken from "../payment-field/use-card-token";
import { ScreeningPaymentType } from "../payment-types";
import "./hpv-upfront-payments.css";

interface UpfrontPaymentsProps {
    onSelectFollowUp: (selectedFollowUp: FollowUpType) => void;
    onCompletePayment: (
        hasValidCreditCard: boolean,
        resolvedFollowUpType: FollowUpType
    ) => Promise<void> | void;
}

type HpvCardPaymentProps = UpfrontPaymentsProps & {
    isBusy?: boolean;
};

function HpvCardPayment({
    onSelectFollowUp,
    onCompletePayment,
    isBusy = false,
}: HpvCardPaymentProps): JSX.Element {
    const [authUser] = useAuthUser();
    const { translations } = useLanguage();
    const cardInfo = useCardPlaceholder();
    const getCardTokenCallback = useCardToken();
    const [analytics] = useAnalytics();
    const t = translations.upfrontPaymentSection.hpv;

    const isUserCardAvailable = !cardInfo.loading && !!cardInfo.result;

    const [isCardFieldValid, setIsCardFieldValid] = useState(
        isUserCardAvailable
    );
    useEffect(() => {
        // Update card field validity whenever async cardInfo loads
        // This ensures that the button's disabled state correctly reflects
        // whether a saved card is available after the API response returns
        if (isUserCardAvailable) {
            setIsCardFieldValid(true);
        }
    }, [isUserCardAvailable]);

    const handleSubmitPayment = useAsyncCallback(async () => {
        if (!authUser || !authUser.lastUserQuestionnaire) {
            return;
        }

        const nextFollowUpType = FollowUpType.Express;
        onSelectFollowUp(nextFollowUpType);

        const cardToken = await getCardTokenCallback();
        if (cardToken) {
            await Axios.put(ENDPOINTS.USER_PAYMENT_INFO, {
                token: cardToken,
            });
        }
        if (cardToken || isUserCardAvailable) {
            const method = cardToken
                ? ScreeningPaymentType.CreditCard
                : ScreeningPaymentType.CardOnFile;
            trackScreeningPaymentSubmitted(analytics, method);
            await onCompletePayment(true, nextFollowUpType);
        } else {
            setIsCardFieldValid(false);
        }
    });

    return (
        <>
            <PaymentFieldComponent
                loading={cardInfo.loading}
                setIsValid={setIsCardFieldValid}
                existingCardInfo={cardInfo.loading ? null : cardInfo.result}
            />

            <Button
                loading={handleSubmitPayment.loading || isBusy}
                onClick={() => handleSubmitPayment.execute()}
                disabled={!isCardFieldValid || isBusy}
            >
                {t.confirmButtonText}
            </Button>
        </>
    );
}

export default function HpvUpfrontPayments({
    onSelectFollowUp,
    onCompletePayment,
}: UpfrontPaymentsProps): JSX.Element {
    const isBookingFlowRedesign = useBookingFlowRedesign();
    const { translations, language } = useLanguage();
    const [analytics] = useAnalytics();
    const t = translations.upfrontPaymentSection.hpv;
    const appointmentHold = getAppointmentHold();
    const [isWalletProcessing, setIsWalletProcessing] = useState(false);

    const handleWalletPaid = async (method: string) => {
        const nextFollowUpType = FollowUpType.Express;
        onSelectFollowUp(nextFollowUpType);
        trackScreeningPaymentSubmitted(analytics, method);
        await onCompletePayment(true, nextFollowUpType);
    };

    const description =
        buildReservedAppointmentDescription(
            appointmentHold?.datetime,
            appointmentHold?.clinicName,
            language,
            t.description
        ) ?? t.descriptionFallback;

    const paymentContent = (
        <>
            <div>
                <h2>{t.title}</h2>

                <div className="hpv-upfront-payments__description">
                    {description}
                </div>
            </div>
            <div className="hpv-upfront-payments__fee-list">
                <div className="hpv-upfront-payments__fee-item">
                    <div className="hpv-upfront-payments__fee-details">
                        <div className="hpv-upfront-payments__fee-timing">
                            {t.now}
                        </div>
                        <div className="hpv-upfront-payments__fee-label">
                            {t.consultationText}
                        </div>
                    </div>
                    <strong className="hpv-upfront-payments__fee-price">
                        {t.consultationPrice}
                    </strong>
                </div>
                <hr className="hpv-upfront-payments__divider" />
                <div className="hpv-upfront-payments__fee-item">
                    <div className="hpv-upfront-payments__fee-details">
                        <div className="hpv-upfront-payments__fee-timing">
                            {t.inClinic}
                        </div>
                        <div className="hpv-upfront-payments__fee-label">
                            {t.analysisText}
                        </div>
                    </div>
                    <div className="hpv-upfront-payments__fee-price">
                        {t.analysisPrice}
                    </div>
                </div>
            </div>

            <div>
                <PaymentMethodsBusy
                    busy={isWalletProcessing}
                    label={
                        translations.upfrontPaymentSection.paymentForm
                            .walletProcessing
                    }
                >
                    <ExpressWalletCheckout
                        flow={WalletCheckoutFlow.ExpressPayHpv}
                        onPaid={handleWalletPaid}
                        onProcessingChange={setIsWalletProcessing}
                    />
                    <CardElementsProvider>
                        <HpvCardPayment
                            onSelectFollowUp={onSelectFollowUp}
                            onCompletePayment={onCompletePayment}
                            isBusy={isWalletProcessing}
                        />
                    </CardElementsProvider>
                </PaymentMethodsBusy>
            </div>
            <BookingFlowPaymentFooterDisclaimers
                variant="hpvLegacy"
                cancellationText={t.cancellationOrReschedulingFeeWarning}
                showDelayWarning={false}
            />
        </>
    );

    if (isBookingFlowRedesign) {
        const dueSummary = getHpvBookingFlowDueSummary(t);

        return (
            <BookingFlowNarrowContent className="hpv-upfront-payments hpv-upfront-payments--bookingFlow">
                <BookingFlowPageHeading
                    title={t.title}
                    subtitle={description}
                    size="large"
                    subtitleFullWidth
                    tightSubtitle
                />
                <BookingFlowScreeningPayment
                    dueSummary={dueSummary}
                    walletCheckoutFlow={WalletCheckoutFlow.ExpressPayHpv}
                    paymentFollowUpType={FollowUpType.Express}
                    onSelectFollowUp={onSelectFollowUp}
                    onCompletePayment={onCompletePayment}
                    showDelayWarning={false}
                />
            </BookingFlowNarrowContent>
        );
    }

    return (
        <div className="container--narrow hpv-upfront-payments">
            {paymentContent}
        </div>
    );
}
